import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import axios from "./axios";
import { localizedApiError } from "../utils/apiError";

export const userKeys = {
  currentUser: () => ["currentUser"] as const,
  newUser: () => ["newUser"] as const,
};

export interface RequestLogin {
  email: string;
  password: string;
}

/**
 * Pourquoi la connexion peut aboutir sans ouvrir l'application.
 *
 * `ok`                 rattachement actif, on entre.
 * `pending_invitation` une invitation utilisable attend. En pratique on ne
 *                      devrait plus voir cet etat apres un login : l'API
 *                      rattache elle-meme les invitations en attente pendant
 *                      l'authentification. Il reste traite ici parce qu'un
 *                      client deploye devant l'API ne doit pas s'effondrer.
 * `no_membership`      aucun rattachement, aucune invitation exploitable.
 */
export type OnboardingState = "ok" | "pending_invitation" | "no_membership";

export interface LoginDenial {
  state: OnboardingState;
  email: string;
}

/**
 * Determine si l'application peut s'ouvrir.
 *
 * L'ETAT VIENT DU SERVEUR, pas d'une deduction locale. Cet ecran decidait
 * lui-meme en parcourant `company_users`, ce qui avait trois consequences :
 * l'API repondait 200 sur un refus, rien n'etait journalise cote serveur, et
 * le seul temoin de l'echec etait la personne devant l'ecran. Un partenaire
 * est reste bloque trois jours avant que l'information ne remonte, par
 * message.
 *
 * Le repli sur `company_users` reste la uniquement pour le cas ou ce client
 * serait deploye avant l'API. Il disparaitra une fois les deux alignes.
 */
const resolveOnboardingState = (user: any): OnboardingState => {
  if (user?.onboarding_state) {
    return user.onboarding_state as OnboardingState;
  }

  const isFleetManager = (user?.company_users ?? []).some(
    (cu: any) => cu?.company_role === "FLEET_MANAGER" && Number(cu?.company_id) > 0
  );

  return isFleetManager ? "ok" : "no_membership";
};

/**
 * @param onDenied Appele quand l'authentification reussit mais que le compte
 *   n'ouvre aucune flotte. L'appelant affiche alors un ecran qui nomme la cause
 *   ET propose une suite -- l'ancienne version se contentait d'un toast rouge
 *   "vous n'etes pas un fleet manager", un etat terminal dont ni l'utilisateur
 *   ni son gestionnaire ne pouvaient sortir par l'interface.
 */
export const useUserLogin = (onDenied?: (denial: LoginDenial) => void) => {
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.currentUser(),
    mutationFn: (newUser: RequestLogin) => {
      return axios.post("/user/login", JSON.stringify(newUser));
    },
    onSuccess: (data, variables) => {
      const { data: user } = data;
      const state = resolveOnboardingState(user);

      if (state === "ok") {
        localStorage.setItem("currentUser", JSON.stringify(user));
        // SECURITY (VULN-02) : l'API ne renvoie plus la liste des sessions
        // (elle exposait les jetons de toutes les sessions). Le jeton de la
        // session courante est desormais a la racine : user.token.
        localStorage.setItem("token", user?.token ?? user?.sessions?.[0]?.token);
        toast.success(t("login.success", "Authentication successful!"));
        /*
         * Une seule instruction, et elle nomme sa destination.
         *
         * C'etait `window.location.reload()` PUIS `navigate("/dashboard")`. Le
         * rechargement gagne toujours la course, donc le navigate ne s'executait
         * jamais : on retombait sur l'URL courante et c'etait la redirection de
         * Routes.tsx (`isAuthentificated ? <Navigate to="/dashboard">`) qui
         * finissait le travail. Cela marche depuis "/" et nulle part ailleurs --
         * en particulier pas depuis /invitation, ou l'utilisateur venait de
         * s'authentifier et se retrouvait a relire sa propre invitation.
         *
         * `href` recharge ET va au bon endroit, ce qui rend cette fonction
         * utilisable depuis n'importe quel ecran.
         */
        window.location.href = "/dashboard";
        return;
      }

      /*
       * Session non conservee sur un refus.
       *
       * Garder le jeton laisserait un utilisateur a moitie connecte : les
       * gardes de routes verraient un token en localStorage et le laisseraient
       * entrer sur des ecrans que l'API refuse ensuite un par un, en 403.
       */
      localStorage.removeItem("token");
      localStorage.removeItem("currentUser");

      onDenied?.({ state, email: variables?.email ?? "" });
    },
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("login.failed", "Sign-in failed"))
      );
    },
  });
};

export interface RequestRegisterFleetUser {
  email: string;
  password: string;
  code: string;
  "password-confirmation": string;
  company: string;
  // Optional: sent by the invitation landing page for EMPLOYEE invitees
  // whose account doesn't exist yet (the backend creates it on the fly).
  firstname?: string;
  lastname?: string;
}

/**
 * Accepter l'invitation et poser son mot de passe.
 *
 * ── LA SESSION RENVOYEE PAR L'API EST DESORMAIS CONSERVEE ─────────────────
 *
 * `UserController@addFleetAdmin` termine par `$this->service->login(...)` : la
 * reponse contient donc deja l'utilisateur complet et son jeton de session.
 * `onSuccess` l'ignorait entierement (`onSuccess: (_) => toast(...)`), et
 * l'ecran d'invitation affichait ensuite un ecran de succes dont le seul bouton
 * renvoyait vers la page de connexion.
 *
 * Autrement dit : le parcours nominal se terminait par "retapez le mot de passe
 * que vous venez de choisir il y a huit secondes". Le serveur avait fait le
 * travail, le client le jetait.
 */
export const useRegisterFleetUser = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.newUser(),
    mutationFn: async (user: RequestRegisterFleetUser) => {
      const data = await axios.post("/user/register-fleet-user", user);
      return data;
    },
    onSuccess: (response: any) => {
      const user = response?.data;
      // SECURITY (VULN-02) : jeton a la racine (user.token), plus dans sessions[].
      const token = user?.token ?? user?.sessions?.[0]?.token;

      /*
       * On ne conserve la session que si le rattachement a bien eu lieu.
       *
       * Meme raison que dans useUserLogin : un jeton en localStorage sans
       * societe ouverte laisse un utilisateur a moitie connecte, que les gardes
       * de routes laissent entrer et que l'API refuse ensuite ecran par ecran
       * en 403. Sur ce parcours acceptInvitation() cree le company_user lui-meme,
       * donc l'etat doit valoir "ok" -- s'il ne le vaut pas, quelque chose ne
       * s'est pas passe et la page de connexion reste la bonne sortie.
       */
      if (token && resolveOnboardingState(user) === "ok") {
        localStorage.setItem("currentUser", JSON.stringify(user));
        localStorage.setItem("token", token);
      }

      toast.success(t("login.registered", "You are registered"));
    },
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("common.errorOccurred", "Something went wrong"))
      );
    },
  });
};

/**
 * Envoyer un code de reinitialisation a une adresse donnee.
 *
 * Distinct de `useSendCode`, qui est cable sur l'ecran "mot de passe oublie" et
 * navigue tout seul. Ici l'appelant (l'ecran d'invitation) choisit la suite, et
 * on vise la route `reset-password` pour que le mail parle bien de
 * reinitialisation et non de creation de compte.
 */
export const useSendResetCode = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.newUser(),
    mutationFn: (params: RequestSendCode) =>
      axios.post("/user/send-code/reset-password", params),
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("common.errorOccurred", "Something went wrong"))
      );
    },
  });
};

export interface RequestSendCode {
  email: string;
}

export const useSendCode = (email: string) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.newUser(),
    mutationFn: (user: RequestSendCode) => {
      return axios.post("/user/send-code", user);
    },
    onSuccess: () => {
      navigate(`/enter_code_forgot_password?email=${email}`);
      toast.success(t("common.emailSend", "Email send"));
    },
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("common.errorOccurred", "Something went wrong"))
      );
    },
  });
};

export interface RequestConfirmCode {
  email: string;
  code: string;
}

export const useConfirmCode = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.newUser(),
    mutationFn: (user: RequestConfirmCode) => {
      return axios.post("/user/add", user);
    },
    onSuccess: (data: any) => {
      localStorage.setItem("token", data?.data?.token);
      navigate(`/forgot_password?idUser=${data?.data?.id}`);
      toast.success(t("common.validCode", "Valid code"));
    },
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("common.errorOccurred", "Something went wrong"))
      );
    },
  });
};

export interface RequestConfirmUpdatePassword {
  id: number;
  password: string;
}

export const useConfirmUpdatePassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.newUser(),
    mutationFn: (user: RequestConfirmUpdatePassword) => {
      return axios.put("/user/update", user);
    },
    onSuccess: (_) => {
      localStorage.clear();
      navigate("/");
      toast.success(t("login.passwordReseted", "Password reset"));
    },
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("common.errorOccurred", "Something went wrong"))
      );
    },
  });
};

export interface RequestUpdatePassword {
  id: number;
  password: string;
}

/**
 * Change the authenticated user's password from Settings.
 *
 * Backend: PUT /user/update { id, password } (the same endpoint the
 * forgot-password flow ends with). Unlike useConfirmUpdatePassword, this keeps
 * the user signed in: the session token is unaffected by the password change,
 * so there is no need to clear storage or redirect to the login screen.
 */
export const useUpdatePassword = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  return useMutation({
    mutationKey: userKeys.currentUser(),
    mutationFn: (payload: RequestUpdatePassword) => {
      return axios.put("/user/update", payload);
    },
    onSuccess: () => {
      onSuccess?.();
      toast.success(t("login.passwordReseted", "Password reset"));
    },
    onError: (err: any) => {
      toast.error(
        localizedApiError(t, err, "errorLogin", t("common.errorOccurred", "Something went wrong"))
      );
    },
  });
};